import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Handle, Position, useReactFlow, useNodes } from 'reactflow';
import { ExclamationTriangleIcon, Pencil1Icon, Pencil2Icon } from '@radix-ui/react-icons';
import { MdOutlineWindPower } from "react-icons/md";
import './WindGenerationNode.css';
import WindGenerationConfigModal from './WindGenerationConfigModal';
import EditGenerationProfileModal from '../../EditGenerationProfileModal/EditGenerationProfileModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { getDocumentById } from '../../../api/api';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';


const WindGenerationNode = ({ id, data, selected }) => {
  const { name, deviceInfo, file_path } = data;
  const filePath = file_path || (data.node_params && data.node_params[0] && data.node_params[0].file_path);
  const mwPerTurbine = data.mw_per_turbine || (data.node_params && data.node_params[0] && data.node_params[0].mw_per_turbine) || 2.1;
  const [deviceData, setDeviceData] = useState(deviceInfo || data.deviceInfo);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProfileEditOpen, setIsProfileEditOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const reactFlowInstance = useReactFlow();
  const nodes = useNodes();
  const { addWindGenerationNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping } = useContext(NodeMappingContext);

  const isConfigured = data.isConfigured || data.isApiConfigured;

  const profileObjectId = filePath
    ? filePath.split('/').pop().replace('.csv', '')
    : (deviceData?._id || null);

  useEffect(() => {
    console.log(`WindGenerationNode ${id} received data:`, data);
  }, [id, data]);

  useEffect(() => {
    const fetchDocumentInfo = async () => {
      if (filePath && isConfigured && !deviceData) {
        try {
          setIsLoadingDocument(true);
          const objectId = filePath.split('/').pop().replace('.csv', '');
          console.log(`Extracting object ID from file path: ${filePath} -> ${objectId}`);

          if (objectId) {
            const documentData = await getDocumentById(objectId);
            if (documentData) {
              console.log(`Retrieved document data for wind node ${id}:`, documentData);

              const formattedDeviceData = {
                _id: documentData._id || objectId,
                makeAndModel: documentData.makeAndModel,
                location: documentData.location,
                sub_location: documentData.sub_location,
                poe: documentData.poe,
                maxRange: documentData.maxRange,
                startDate: documentData.start_Date,
                endDate: documentData.end_Date,
                start_Date: documentData.start_Date,
                end_Date: documentData.end_Date,
                interval: documentData.interval,
                type: documentData.type || 'wind',
                mw_per_turbine: mwPerTurbine,
                is_test: documentData.is_test !== false,
              };

              setDeviceData(formattedDeviceData);

              reactFlowInstance.setNodes((nodes) =>
                nodes.map((node) => {
                  if (node.id === id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        deviceInfo: formattedDeviceData
                      }
                    };
                  }
                  return node;
                })
              );
            }
          }
        } catch (error) {
          console.error(`Error fetching document for wind node ${id}:`, error);
        } finally {
          setIsLoadingDocument(false);
        }
      }
    };

    fetchDocumentInfo();
  }, [id, filePath, isConfigured, deviceData, reactFlowInstance, mwPerTurbine]);

  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  const openConfigModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeConfigModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const openProfileEditModal = useCallback(() => {
    setIsProfileEditOpen(true);
  }, []);

  const closeProfileEditModal = useCallback(() => {
    setIsProfileEditOpen(false);
  }, []);

  const handleProfileSaved = useCallback((updatedDoc) => {
    const formattedDeviceData = {
      _id: updatedDoc._id || profileObjectId,
      makeAndModel: updatedDoc.makeAndModel,
      location: updatedDoc.location,
      sub_location: updatedDoc.sub_location,
      poe: updatedDoc.poe,
      maxRange: updatedDoc.maxRange,
      startDate: updatedDoc.start_Date || updatedDoc.startDate,
      endDate: updatedDoc.end_Date || updatedDoc.endDate,
      start_Date: updatedDoc.start_Date || updatedDoc.startDate,
      end_Date: updatedDoc.end_Date || updatedDoc.endDate,
      interval: updatedDoc.interval,
      type: updatedDoc.type || 'wind',
      mw_per_turbine: mwPerTurbine,
      is_test: updatedDoc.is_test !== false,
    };
    setDeviceData(formattedDeviceData);
    reactFlowInstance.setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              deviceInfo: formattedDeviceData,
            },
          };
        }
        return node;
      })
    );
  }, [id, profileObjectId, reactFlowInstance, mwPerTurbine]);

  const handleConfigured = useCallback(async (configData) => {
    setIsConfiguring(true);
    try {
      const editing = isConfigured === true;
      const { name } = configData;
      const position = getNodePosition();

      if (editing) {
        const nodePath = configData.file_path || filePath;
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  file_path: nodePath,
                  mw_per_turbine: configData.mw_per_turbine ?? mwPerTurbine,
                  deviceInfo: undefined,
                  isApiConfigured: true
                }
              };
            }
            return node;
          })
        );
        setDeviceData(undefined);
        setIsConfiguring(false);
        closeConfigModal();
        return;
      }

      console.log(`Configuring wind generation node ${id} -> ${name}:`, { configData, position });
      updateNodeMapping(id, name);
      const nodePath = configData.file_path || `./gen_upload/${name}.csv`;
      addWindGenerationNode(name, null, { x: position.x, y: position.y }, nodePath, configData.mw_per_turbine ?? mwPerTurbine);

      setTimeout(() => {
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                id: name,
                data: {
                  ...node.data,
                  name,
                  deviceInfo: configData.deviceInfo,
                  file_path: nodePath,
                  mw_per_turbine: configData.mw_per_turbine ?? mwPerTurbine,
                  isApiConfigured: true
                }
              };
            }
            return node;
          })
        );
        updateSelectedNode(name);
        setTimeout(async () => {
          try {
            const objectId = (configData.file_path || nodePath).split('/').pop().replace('.csv', '');
            const documentData = await getDocumentById(objectId);
            if (documentData) {
              const formattedDeviceData = {
                _id: documentData._id || objectId,
                makeAndModel: documentData.makeAndModel,
                location: documentData.location,
                sub_location: documentData.sub_location,
                poe: documentData.poe,
                maxRange: documentData.maxRange,
                startDate: documentData.start_Date,
                endDate: documentData.end_Date,
                start_Date: documentData.start_Date,
                end_Date: documentData.end_Date,
                interval: documentData.interval,
                type: documentData.type || 'wind',
                mw_per_turbine: configData.mw_per_turbine ?? mwPerTurbine,
                is_test: documentData.is_test !== false,
              };
              setDeviceData(formattedDeviceData);
              reactFlowInstance.setNodes((nodes) =>
                nodes.map((node) => {
                  if (node.id === name) {
                    return { ...node, data: { ...node.data, deviceInfo: formattedDeviceData } };
                  }
                  return node;
                })
              );
            }
          } catch (error) {
            console.error(`Error fetching document for newly configured wind node:`, error);
          } finally {
            setIsConfiguring(false);
            closeConfigModal();
          }
        }, 100);
      }, 100);
    } catch (error) {
      console.error('Error configuring wind generation node:', error);
      notifyError(error, { operation: isConfigured ? OP.node.editWindGeneration : OP.node.appendWindGeneration });
      setIsConfiguring(false);
      closeConfigModal();
    }
  }, [id, isConfigured, filePath, reactFlowInstance, getNodePosition, closeConfigModal, updateNodeMapping, addWindGenerationNode, updateSelectedNode, mwPerTurbine]);

  return (
    <>
      <div
        className={`wind-node ${selected ? 'wind-selected' : ''} ${isConfigured ? 'wind-configured' : 'wind-not-configured'}`}
      >
        <div className={`wind-node-header ${isConfigured ? 'wind-configured' : 'wind-not-configured'}`}>
          <div className="wind-node-header-title">
            <div className="wind-node-type">Wind Generation</div>
            {name && isConfigured && <div className="wind-node-name-display">{name}</div>}
          </div>
          <div className="wind-node-header-icons">
            {!isConfigured && (
              <button
                onClick={openConfigModal}
                className="wind-config-button"
                title="Configure Wind Generation Node"
                disabled={isConfiguring}
              >
                <ExclamationTriangleIcon className="wind-icon wind-warning-icon" />
              </button>
            )}
            {isConfigured && (
              <>
                <button
                  onClick={openConfigModal}
                  className="wind-config-button"
                  title="Edit Wind Generation Node"
                  disabled={isConfiguring}
                >
                  <Pencil1Icon className="wind-icon" />
                </button>
                {profileObjectId && (
                  <button
                    onClick={openProfileEditModal}
                    className="wind-config-button"
                    title="Edit uploaded profile metadata"
                    disabled={isConfiguring || isLoadingDocument}
                  >
                    <Pencil2Icon className="wind-icon" />
                  </button>
                )}
              </>
            )}
            <MdOutlineWindPower className="wind-icon" />
          </div>
        </div>
        <div className="wind-node-content">
          <div className="wind-text-xs">
            {!isConfigured && (
              <div className="wind-text-warning">
                Parameters not configured
              </div>
            )}
            {isConfiguring && (
              <div className="wind-text-info">
                Configuring...
              </div>
            )}
            {isConfigured && (
              <>
                {isLoadingDocument && (
                  <div className="wind-text-info">
                    Loading document info...
                  </div>
                )}
                {deviceData && (
                  <>
                    <div className="wind-node-device-info">
                      <div>Data: {deviceData.is_test === false ? 'Actual' : 'Test'}</div>
                      <div>Make: {deviceData.makeAndModel}</div>
                      <div>Location: {deviceData.location}</div>
                      {deviceData.sub_location && <div>Sub-location: {deviceData.sub_location}</div>}
                      {deviceData.poe && <div>PoE: {deviceData.poe}</div>}
                      <div>Range: {deviceData.maxRange}</div>
                      {deviceData.startDate && <div>Start: {deviceData.startDate}</div>}
                      {deviceData.endDate && <div>End: {deviceData.endDate}</div>}
                      <div>MWp/Turbine: {deviceData.mw_per_turbine ?? mwPerTurbine}</div>
                      {deviceData.interval && <div>Interval: {deviceData.interval === 15 || deviceData.interval === '15' ? '15 mins' :
                                                            deviceData.interval === 60 || deviceData.interval === '60' ? '1 hr' :
                                                            `${deviceData.interval} mins`}</div>}
                    </div>
                  </>
                )}
                {!deviceData && !isLoadingDocument && filePath && (
                  <div className="wind-text-info">
                    File: {filePath.split('/').pop()}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="wind-flex-container">
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              className={`wind-connection-point ${isConfigured ? 'wind-configured-output' : 'wind-not-configured'}`}
              isConnectable={isConfigured}
              data-maxconnections="1"
            />
          </div>
        </div>
      </div>

      <WindGenerationConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        initialData={{
          name,
          position: getNodePosition(),
          isConfigured: isConfigured,
          mw_per_turbine: mwPerTurbine,
          is_test: deviceData?.is_test !== false,
        }}
      />

      <EditGenerationProfileModal
        isOpen={isProfileEditOpen}
        onRequestClose={closeProfileEditModal}
        profile={deviceData ? { ...deviceData, _id: deviceData._id || profileObjectId } : { _id: profileObjectId, type: 'wind' }}
        onSaved={handleProfileSaved}
      />
    </>
  );
};

export default WindGenerationNode;
